import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Organization, OrganizationMember } from '@/types/supabase';

export function useOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data: memberRows, error: memberError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (memberError) throw memberError;

      const memberOrgIds = (memberRows || []).map(row => row.organization_id);
      
      const { data: ownedOrgs, error: ownedError } = await supabase
        .from('organizations')
        .select('id')
        .eq('created_by', user.id);

      if (ownedError) throw ownedError;

      const ownedOrgIds = (ownedOrgs || []).map(org => org.id);

      const allOrgIds = [...new Set([...memberOrgIds, ...ownedOrgIds])];

      if (allOrgIds.length === 0) {
        setOrganizations([]);
        setLoading(false);
        return;
      }

      const { data: allOrgs, error: orgsError } = await supabase
        .from('organizations')
        .select('*')
        .in('id', allOrgIds)
        .order('created_at', { ascending: false });

      if (orgsError) throw orgsError;

      const orgsWithCounts = await Promise.all(
        (allOrgs || []).map(async (org: Organization) => {
          const { count, error: countError } = await supabase
            .from('organization_members')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', org.id)
            .eq('status', 'active');

          return { ...org, members_count: countError ? 0 : count || 0 };
        })
      );

      setOrganizations(orgsWithCounts);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch organizations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const createOrganization = useCallback(async (name: string, description?: string) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('organizations')
        .insert({ name, description, created_by: user.id })
        .select()
        .single();

      if (error) throw error;

      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: data.id,
          user_id: user.id,
          role: 'Owner',
          invited_by: user.id,
          status: 'active'
        });

      if (memberError) throw memberError;

      fetchOrganizations();
      toast({ title: 'Success', description: 'Organization created successfully' });
      return data.id;
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({ title: 'Error', description: 'Failed to create organization', variant: 'destructive' });
      return null;
    }
  }, [user, toast, fetchOrganizations]);

  const updateOrganization = useCallback(async (id: string, name: string, description?: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('organizations')
        .update({ name, description, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      fetchOrganizations();
      toast({ title: 'Success', description: 'Organization updated successfully' });
      return true;
    } catch (error) {
      console.error('Error updating organization:', error);
      toast({ title: 'Error', description: 'Failed to update organization', variant: 'destructive' });
      return false;
    }
  }, [user, toast, fetchOrganizations]);

  const getOrganizationMembers = useCallback(async (organizationId: string): Promise<OrganizationMember[]> => {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          *,
          profile:organization_members_user_id_fkey (
            id,
            full_name,
            username,
            avatar_url,
            created_at,
            updated_at
          )
        `)
        .eq('organization_id', organizationId);

      if (error) throw error;
      return (data as OrganizationMember[]) || [];
    } catch (error) {
      console.error('Error fetching organization members:', error);
      return [];
    }
  }, []);

  const inviteUserToOrganization = useCallback(async (
    organizationId: string,
    email: string,
    role: 'Admin' | 'Member' | 'Owner',
    organizationName: string
  ) => {
    if (!user) return false;

    try {
      const { error, data } = await supabase.functions.invoke('invite-user-to-org', {
        body: { organizationId, email, role, organizationName },
        headers: { 'x-supabase-user-id': user.id },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      toast({ title: 'Invitation Sent', description: `An invitation has been sent to ${email}.` });
      return true;
    } catch (error: any) {
      console.error('Error inviting user:', error);
      toast({ title: 'Invitation Failed', description: error.message || 'An unexpected error occurred.', variant: 'destructive' });
      return false;
    }
  }, [user, toast]);

  const acceptInvitation = useCallback(async (organizationId: string) => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ status: 'active', joined_at: new Date().toISOString() })
        .eq('organization_id', organizationId)
        .eq('user_id', user.id)
        .eq('status', 'pending');

      if (error) throw error;
      toast({ title: 'Success!', description: 'You have successfully joined the organization.' });
      fetchOrganizations();
      return true;
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      toast({ title: 'Error', description: error.message || 'Failed to accept invitation.', variant: 'destructive' });
      return false;
    }
  }, [user, toast, fetchOrganizations]);

  const declineInvitation = useCallback(async (organizationId: string) => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('organization_id', organizationId)
        .eq('user_id', user.id)
        .eq('status', 'pending');

      if (error) throw error;
      toast({ title: 'Invitation Declined', description: 'You have declined the invitation.' });
      return true;
    } catch (error: any) {
      console.error('Error declining invitation:', error);
      toast({ title: 'Error', description: error.message || 'Failed to decline invitation.', variant: 'destructive' });
      return false;
    }
  }, [user, toast]);

  const updateMemberRole = useCallback(async (memberId: string, newRole: 'Owner' | 'Admin' | 'Member') => {
    try {
      const { error } = await supabase.from('organization_members').update({ role: newRole }).eq('id', memberId);
      if (error) throw error;
      toast({ title: 'Success', description: 'Member role updated' });
      return true;
    } catch (error) {
      console.error('Error updating member role:', error);
      toast({ title: 'Error', description: 'Failed to update member role', variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const removeMember = useCallback(async (memberId: string) => {
    try {
      const { error } = await supabase.from('organization_members').delete().eq('id', memberId);
      if (error) throw error;
      toast({ title: 'Success', description: 'Member removed' });
      return true;
    } catch (error) {
      console.error('Error removing member:', error);
      toast({ title: 'Error', description: 'Failed to remove member', variant: 'destructive' });
      return false;
    }
  }, [toast]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return {
    organizations,
    loading,
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    getOrganizationMembers,
    inviteUserToOrganization,
    acceptInvitation,
    declineInvitation,
    updateMemberRole,
    removeMember
  };
}